import type { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'

export type Task = {
  taskKey: string
  title: string
  aiQuestion: string | null
  description: string | null
  enableVoiceTour: boolean
  helpCenterUrl: string | null
  videoUrl: string | null
  isMandatory: boolean
}

export type Category = {
  id: string
  categoryKey: string
  title: string
  moduleName: string | null
  dependsOn: string[]
  tasks: Task[]
}

export const CORE_CATEGORY_ID = 'core'

export const getTaskActionUrl = (
  taskKey: string,
  navigation: Navigation<NavigationPaths>
): string | null => {
  switch (taskKey) {
    // Core
    case 'create_legal_entity':
      return navigation.settings.details.fullPath()
    case 'workplaces_and_public_holidays':
      return navigation.settings.locations.fullPath()
    case 'add_employee':
      return navigation.employees.list.fullPath()
    case 'create_job_position':
      return navigation.employees.jobCatalog.fullPath()
    case 'manage_contracts':
      return navigation.employees.list.fullPath()
    case 'view_organizational_chart':
      return navigation.employees.organizationChart.fullPath()
    case 'custom_fields_and_tables':
      return navigation.settings.customizations.fullPath()
    case 'create_teams':
      return navigation.employees.teams.fullPath()
    case 'basic_employee_data':
      return navigation.employees.list.fullPath()
    case 'customize_company_workspace':
      return navigation.settings.customizations.fullPath()
    // Time tracking
    case 'create_time_tracking_policy':
    case 'configure_time_tracking_tolerance':
    case 'configure_extra_time_compensation':
      return navigation.settings.time_tracking.fullPath()
    case 'create_breaks':
      return navigation.settings.time_categories_settings.fullPath()
    case 'create_work_schedules':
      return navigation.settings.workSchedule.fullPath()
    case 'special_hours':
      return navigation.settings.time_tracking.fullPath()
    case 'export_time_tracking_reports':
      return navigation.timeTracking.list.fullPath()
    // Time off
    case 'create_time_off_policy':
    case 'configure_fixed_balance_allowance':
      return navigation.settings.timeoffPolicies.fullPath()
    case 'create_absence_types':
      return navigation.settings.timeoffPolicies.absenceTypes.fullPath()
    case 'create_time_off_approval':
      return navigation.settings.timeoffPolicies.approvalGroups.fullPath()
    case 'create_allowance_adjustments':
      return navigation.employees.fullPath()
    case 'set_up_blocked_periods':
      return navigation.settings.timeoffPolicies.blockedPeriods.fullPath()
    case 'export_time_off_report':
      return navigation.employees.list.fullPath()
    // Documents
    case 'configure_documents_settings':
      return navigation.files.companyFiles.fullPath()
    case 'create_document_templates':
      return navigation.files.documentTemplates.fullPath()
    case 'configure_fillable_pdfs':
      return navigation.files.documentTemplates.fullPath()
    case 'e_signature':
      return navigation.documents.list.fullPath()
    case 'send_documents_in_bulk':
      return navigation.files.bulkFiles.fullPath()
    case 'upload_public_and_internal_documents':
      return navigation.files.companyFiles.fullPath()
    // Permissions & Security
    case 'configure_permissions':
      return navigation.settings.permissions.fullPath()
    case 'configure_trust_channel':
      return navigation.settings.complaints.fullPath()
    // Projects
    case 'create_project':
    case 'explore_project_management':
      return navigation.projects.list.fullPath()
    case 'view_export_project_data':
      return navigation.projects.list.fullPath()
    case 'flexible_project_tracking':
      return navigation.myProjects.fullPath()
    // Shifts management
    case 'create_shifts':
    case 'edit_delete_shifts':
    case 'configure_shifts_coverage':
      return navigation.shifts.fullPath()
    case 'create_rotating_shifts':
      return navigation.shifts.templates.fullPath()
    case 'configure_saved_shifts':
      return navigation.shifts.savedShifts.fullPath()
    case 'export_planned_shifts':
      return navigation.shifts.fullPath()
    // Workflows
    case 'create_workflow':
      return navigation.processes.fullPath()
    case 'explore_onboarding_space':
      return navigation.employees.list.fullPath()
    // Benefits
    case 'creating_stripe_bank_account_and_transferring_funds':
      return navigation.finance.cards.fullPath()
    case 'requesting_factorial_virtual_cards':
      return navigation.finance.cards.fullPath()
    // Expenses
    case 'add_expense':
      return navigation.expenses.list.fullPath()
    case 'setup_expense_approval':
      return navigation.settings.expenses.fullPath()
    case 'configure_expense_form':
      return navigation.settings.expenses.fullPath()
    // Procurement
    case 'configure_accounts_payable':
    case 'add_and_import_invoices':
    case 'pay_invoices_and_reconcile_transactions':
    case 'chart_of_accounts_and_general_ledger':
      return navigation.finance.invoices.fullPath()
    case 'add_bank_accounts':
      return navigation.finance.treasury.banking.accounts.fullPath()
    case 'import_vendors_and_link_accounts':
      return navigation.finance.spending.vendors.fullPath()
    // Recruitment
    case 'create_job_opening':
      return navigation.ats.jobs.fullPath()
    case 'configure_company_page':
      return navigation.settings.company_page.fullPath()
    case 'explore_recruitment':
      return navigation.ats.jobs.fullPath()
    case 'candidate_database':
      return navigation.ats.candidates.fullPath()
    case 'import_job_board_contracts':
      return navigation.settings.ats.integrations.fullPath()
    default:
      return null
  }
}
